// whatsapp-web-api.ts — typed client for the WhatsApp linked-device (QR) channel.
//
// A QR channel is a normal public.api_configurations row (api_provider = 'whatsapp',
// transport = 'web') plus one public.whatsapp_web_sessions row that the bridge service
// keeps current. This module is the only place the screen talks to those two objects.
//
// Hard rule: the screen never selects the encrypted credentials column. Every read below names its
// columns explicitly (never `*`), and the column list is exported so a test can assert
// the secret column is not in it.
//
// The generated Supabase types are produced from production, where these objects do not
// exist yet, so the client below is typed by hand and reached through a narrow cast.

import { supabase } from '@/integrations/supabase/client';
import type { ChannelColor } from '@/lib/whatsapp-channel-color';
import { setServerClock } from '@/lib/server-clock';

export const WEB_SESSION_STATUSES = [
  'waiting_qr',
  'qr_pending',
  'connecting',
  'connected',
  'qr_expired',
  'disconnected',
  'logged_out',
  'replaced',
  'banned',
] as const;

export type WebSessionStatus = (typeof WEB_SESSION_STATUSES)[number];

export type ChannelTransport = 'cloud_api' | 'web';

/** One whatsapp_web_sessions row, minus every secret column. */
export interface WebSession {
  api_configuration_id: string;
  tenant_id: string;
  desired_state: 'linked' | 'unlinked';
  link_requested_at: string | null;
  status: WebSessionStatus;
  qr_code: string | null;
  qr_generated_at: string | null;
  phone_number: string | null;
  last_connected_at: string | null;
  last_disconnect_at: string | null;
  last_disconnect_reason: string | null;
  updated_at: string | null;
  /** The bridge renews its lease on the channel every few seconds while it is alive. */
  lease_expires_at?: string | null;
  /** From the channel row, so the status can say when nobody answers its messages. */
  assigned_agent_id?: string | null;
  assigned_agent_name?: string | null;
}

/** The only columns the screen reads. The encrypted credentials column is deliberately absent. */
export const WEB_SESSION_COLUMNS = [
  'api_configuration_id',
  'tenant_id',
  'desired_state',
  'link_requested_at',
  'status',
  'qr_code',
  'qr_generated_at',
  'phone_number',
  'last_connected_at',
  'last_disconnect_at',
  'last_disconnect_reason',
  'updated_at',
  'lease_expires_at',
] as const;

// The channel row rides along (assistant) through the foreign key, again by explicit columns.
const WEB_SESSION_SELECT = `${WEB_SESSION_COLUMNS.join(', ')}, api_configurations(assigned_agent_id, assigned_agent_name)`;

// Narrow, hand-written view of the client for objects missing from the generated types.
interface UntypedQuery {
  select: (columns: string) => UntypedQuery;
  eq: (column: string, value: string) => UntypedQuery;
  then: PromiseLike<{ data: unknown; error: { message: string } | null }>['then'];
}
interface UntypedClient {
  from: (table: string) => UntypedQuery;
  rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
}
const db = supabase as unknown as UntypedClient;

// Several hooks on one screen (sidebar indicator, space alarm, account list) read the same
// sessions at once: concurrent reads of one scope share a single round trip.
const inflightSessionReads = new Map<string, Promise<WebSession[]>>();

/** `tenantId` undefined reads every space the caller may see (platform admin console). */
export function fetchWebSessions(tenantId?: string): Promise<WebSession[]> {
  const key = tenantId ?? '*';
  const pending = inflightSessionReads.get(key);
  if (pending) return pending;
  const read = readWebSessions(tenantId).finally(() => {
    inflightSessionReads.delete(key);
  });
  inflightSessionReads.set(key, read);
  return read;
}

async function readWebSessions(tenantId?: string): Promise<WebSession[]> {
  const query = db.from('whatsapp_web_sessions').select(WEB_SESSION_SELECT);
  const sentAt = Date.now();
  // The server's time rides along: the lease in these rows is judged on the clock that wrote it.
  const [{ data, error }, clock] = await Promise.all([
    Promise.resolve(tenantId ? query.eq('tenant_id', tenantId) : query),
    db.rpc('whatsapp_web_server_time', {}),
  ]);
  if (!clock.error && typeof clock.data === 'string') setServerClock(clock.data, sentAt, Date.now());
  if (error) throw new Error(error.message);
  const rows = (data as Array<WebSession & { api_configurations?: { assigned_agent_id: string | null; assigned_agent_name: string | null } | null }> | null) ?? [];
  return rows.map(({ api_configurations: channel, ...session }) => ({
    ...session,
    assigned_agent_id: channel?.assigned_agent_id ?? null,
    assigned_agent_name: channel?.assigned_agent_name ?? null,
  }));
}

export async function createWhatsAppWebChannel(
  tenantId: string,
  accountDisplayName: string,
  assignedAgentId: string | null,
): Promise<string> {
  const { data, error } = await db.rpc('create_whatsapp_web_channel', {
    p_tenant_id: tenantId,
    p_account_display_name: accountDisplayName,
    p_assigned_agent_id: assignedAgentId,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function requestWhatsAppWebLink(apiConfigurationId: string): Promise<void> {
  const { error } = await db.rpc('request_whatsapp_web_link', { p_api_configuration_id: apiConfigurationId });
  if (error) throw new Error(error.message);
}

export async function unlinkWhatsAppWeb(apiConfigurationId: string): Promise<void> {
  const { error } = await db.rpc('unlink_whatsapp_web', { p_api_configuration_id: apiConfigurationId });
  if (error) throw new Error(error.message);
}

/** Spaces where a QR channel may be created: the number can be banned by Meta, so only demo and
 * test spaces qualify, or a space a platform admin flagged (`feature_overrides.whatsapp_web_channel`).
 * Mirrors the guard inside create_whatsapp_web_channel, which stays the real one. */
export function canCreateWebChannel(spaceNature: string | null | undefined, featureOverrides: unknown): boolean {
  if (spaceNature === 'demo' || spaceNature === 'test') return true;
  return (featureOverrides as { whatsapp_web_channel?: unknown } | null | undefined)?.whatsapp_web_channel === true;
}

/** What the screen shows: the bridge's status, or a condition derived from the row. */
export type WebDisplayStatus = WebSessionStatus | 'bridge_down' | 'no_assistant';

export interface WebStatusPresentation {
  /** Same colour vocabulary as the Meta health badge, so the two never contradict. */
  color: ChannelColor;
  /** Key under whatsappConfig:webChannel.status.* */
  labelKey: WebDisplayStatus;
}

const PRESENTATION: Record<WebDisplayStatus, ChannelColor> = {
  connected: 'green',
  no_assistant: 'amber',
  waiting_qr: 'amber',
  qr_pending: 'amber',
  connecting: 'amber',
  bridge_down: 'red',
  qr_expired: 'red',
  disconnected: 'red',
  logged_out: 'red',
  replaced: 'red',
  banned: 'red',
};

export function presentWebStatus(status: WebDisplayStatus): WebStatusPresentation {
  return { color: PRESENTATION[status] ?? 'indeterminate', labelKey: status };
}

/**
 * The status a person should read. Three facts override what the row says on its own:
 *  - a session that needs the bridge (a pending code, a connection in progress, a connected
 *    phone) whose lease ran out has nobody working on it: the bridge does not answer;
 *  - a connected phone with no assistant receives messages nobody answers, so it is not green.
 */
/**
 * How long a freshly requested code may wait for the bridge to take the channel before the screen
 * says nobody is working on it. The bridge renews its leases every 30 s (the channel contract), so
 * a bridge that has not claimed a channel within one renewal period is not serving it.
 */
export const BRIDGE_CLAIM_GRACE_MS = 30_000;

export function effectiveWebStatus(
  session: Pick<WebSession, 'status' | 'lease_expires_at' | 'assigned_agent_id' | 'link_requested_at'>,
  now: number,
): WebDisplayStatus {
  const lease = session.lease_expires_at ? Date.parse(session.lease_expires_at) : 0;
  const leaseAlive = lease > now;
  const needsBridge = session.status === 'connected' || session.status === 'connecting' || session.status === 'qr_pending';
  if (needsBridge && !leaseAlive) return 'bridge_down';
  if (session.status === 'waiting_qr' && !leaseAlive) {
    // Nobody holds the channel yet. Right after the request that is normal; past one renewal
    // period it means no bridge is running, and the screen must not spin forever.
    const requested = session.link_requested_at ? Date.parse(session.link_requested_at) : null;
    if (requested !== null && now - requested > BRIDGE_CLAIM_GRACE_MS) return 'bridge_down';
  }
  if (session.status === 'connected' && !session.assigned_agent_id) return 'no_assistant';
  return session.status;
}

export type RecoveryKind = 'show_code' | 'new_code' | 'unlink' | 'cancel_link';

/**
 * The one action that gets a channel out of its current state, or null when there is none.
 * `unlink` is for an account whose phone is linked; `cancel_link` (same RPC) is for one that is
 * not linked yet, so the action never contradicts what the row says.
 */
export function recoveryFor(status: WebDisplayStatus, rawStatus?: WebSessionStatus): RecoveryKind | null {
  switch (status) {
    case 'waiting_qr':
    case 'qr_pending':
      return 'show_code';
    case 'qr_expired':
    case 'disconnected':
    case 'logged_out':
    case 'replaced':
      return 'new_code';
    case 'connecting':
    case 'banned':
      return 'cancel_link';
    case 'bridge_down':
      return rawStatus === 'connected' ? 'unlink' : 'cancel_link';
    default:
      return null;
  }
}

/** A new code can be requested once the current one is gone or the link is down. */
export function canRequestNewCode(status: WebSessionStatus): boolean {
  return status === 'qr_expired' || status === 'disconnected' || status === 'logged_out' || status === 'replaced';
}

/** Statuses in which the bridge is still working toward a connection. */
export function isLinking(status: WebSessionStatus): boolean {
  return status === 'waiting_qr' || status === 'qr_pending' || status === 'connecting';
}

/** Seconds since the QR was requested/generated, for the elapsed timer. */
export function elapsedSeconds(session: Pick<WebSession, 'status' | 'qr_generated_at' | 'link_requested_at'>, now: number): number {
  const since = session.status === 'qr_pending' ? session.qr_generated_at ?? session.link_requested_at : session.link_requested_at;
  if (!since) return 0;
  return Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
}

export function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** The linked number as WhatsApp reports it (digits only), grouped when the country is known. */
export function formatLinkedPhone(phone: string | null): string | null {
  if (!phone) return null;
  if (!/^\d+$/.test(phone)) return phone;
  const chile = /^56(\d)(\d{4})(\d{4})$/.exec(phone);
  return chile ? `+56 ${chile[1]} ${chile[2]} ${chile[3]}` : `+${phone}`;
}

/** Reasons the bridge writes to last_disconnect_reason, as `token` or `token:detail`. */
export const KNOWN_DISCONNECT_REASONS = [
  'unlinked',
  'device_removed',
  'logged_out',
  'replaced_by_another_client',
  'forbidden',
  'qr_not_scanned',
  'start_failed',
  'retries_exhausted',
  'connection_lost',
] as const;

/** The known token of a reason such as `retries_exhausted:428` (the detail after the colon is for
 * logs, not for the screen), or null when the bridge wrote something this screen has no words for. */
export function disconnectReasonToken(reason: string): (typeof KNOWN_DISCONNECT_REASONS)[number] | null {
  const head = reason.split(':')[0];
  return (KNOWN_DISCONNECT_REASONS as readonly string[]).includes(head) ? (head as (typeof KNOWN_DISCONNECT_REASONS)[number]) : null;
}
